import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export { Icon } from './Icon';
export type { IconName } from './Icon';
export { MarqueeBanner } from './Marquee';

/* ---------- Page header ---------- */
export function PageHeader({ eyebrow, title, lede, actions, crumbs }: { eyebrow?: string; title: ReactNode; lede?: ReactNode; actions?: ReactNode; crumbs?: { label: string; href?: string }[] }) {
  return (
    <header className="page-header page-enter">
      <div>
        {crumbs && crumbs.length > 0 && (
          <nav className="breadcrumb" aria-label="Breadcrumb">
            {crumbs.map((c, i) => (
              <span key={`${c.label}-${i}`}>
                {i > 0 && ' / '}
                {c.href ? <a href={c.href}>{c.label}</a> : <span aria-current="page">{c.label}</span>}
              </span>
            ))}
          </nav>
        )}
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

/* ---------- Avatar ---------- */
const hue = (s: string) => (Array.from(s).reduce((a, c) => a + c.charCodeAt(0), 0) % 5) + 1;
export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
  return <span className={`avatar ${size === 'md' ? '' : size} hue-${hue(name)}`} aria-hidden="true">{initials}</span>;
}

/* ---------- Stat and ring ---------- */
export function Stat({ value, label, delta, icon }: { value: ReactNode; label: string; delta?: string; icon?: IconName }) {
  return (
    <div className="stat">
      <div className="row" style={{ gap: '0.4rem' }}>
        {icon && <span className="muted"><Icon name={icon} size={18} /></span>}
        <span className="value">{value}</span>
      </div>
      <span className="label">{label}</span>
      {delta && <span className="delta">{delta}</span>}
    </div>
  );
}

/** Animated count-up for numbers (respects reduced motion). */
export function useCountUp(target: number, duration = 700): number {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(reduced ? target : 0);
  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduced]);
  return value;
}

export function ProgressRing({ value, label, size = 96, tone }: { value: number; label: string; size?: number; tone?: 'ok' | 'bad' }) {
  const pctTarget = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const shown = useCountUp(pctTarget);
  const r = 40;
  const c = 2 * Math.PI * r;
  const cls = ['ring', tone ?? (value >= 0.7 ? 'ok' : value < 0.4 ? 'bad' : '')].filter(Boolean).join(' ');
  return (
    <div className={cls} style={{ ['--size' as string]: `${size}px` }} role="img" aria-label={`${label} ${pctTarget} percent`}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle className="track" cx="50" cy="50" r={r} />
        <circle className="bar" cx="50" cy="50" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - shown / 100)} />
      </svg>
      <span className="value">{shown}%</span>
    </div>
  );
}

/* ---------- Empty state ---------- */
export function EmptyState({ icon = 'sparkles', title, children, actions }: { icon?: IconName; title: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="empty" role="status">
      <span className="icon"><Icon name={icon} size={22} /></span>
      <h3>{title}</h3>
      {children && <p className="small">{children}</p>}
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

/* ---------- Skeletons ---------- */
export function Skeleton({ kind = 'text', width, height, count = 1 }: { kind?: 'text' | 'title' | 'block' | 'circle'; width?: string; height?: string; count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className={`skeleton ${kind}`} style={{ width, height }} aria-hidden="true" />
      ))}
    </>
  );
}
export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="card" aria-hidden="true">
      <Skeleton kind="title" />
      <Skeleton kind="text" count={lines} />
    </div>
  );
}

/* ---------- Toasts ---------- */
export type ToastTone = 'info' | 'ok' | 'warn' | 'bad';
interface ToastItem { id: number; tone: ToastTone; title: string; body?: string }
interface ToastApi { toast: (title: string, opts?: { body?: string; tone?: ToastTone; ttl?: number }) => void }
const ToastContext = createContext<ToastApi>({ toast: () => undefined });
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const toast = useCallback<ToastApi['toast']>((title, opts) => {
    const id = ++seq.current;
    setItems((xs) => [...xs.slice(-3), { id, title, body: opts?.body, tone: opts?.tone ?? 'info' }]);
    window.setTimeout(() => dismiss(id), opts?.ttl ?? 5000);
  }, [dismiss]);
  const api = useMemo(() => ({ toast }), [toast]);
  const iconFor: Record<ToastTone, IconName> = { info: 'info', ok: 'check', warn: 'warning', bad: 'error' };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite" aria-relevant="additions">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`} role="status">
            <Icon name={iconFor[t.tone]} size={18} />
            <div>
              <strong>{t.title}</strong>
              {t.body && <div className="small muted">{t.body}</div>}
            </div>
            <button type="button" className="ghost small close" aria-label="Dismiss notification" onClick={() => dismiss(t.id)}><Icon name="close" size={16} /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export const useToast = () => useContext(ToastContext);

/* ---------- Modal (native dialog) ---------- */
export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="modal" onClose={onClose} aria-labelledby="modal-title">
      <div className="modal-head">
        <h2 id="modal-title">{title}</h2>
        <button type="button" className="ghost icon-button" aria-label="Close dialog" onClick={onClose}><Icon name="close" /></button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-body" style={{ paddingTop: 0 }}><div className="actions" style={{ margin: 0, justifyContent: 'flex-end' }}>{footer}</div></div>}
    </dialog>
  );
}

/* ---------- Section head ---------- */
export function SectionHead({ title, more, moreLabel = 'See all', id }: { title: string; more?: string; moreLabel?: string; id?: string }) {
  return (
    <div className="section-head">
      <h2 id={id}>{title}</h2>
      {more && <a className="more" href={more}>{moreLabel} <Icon name="arrow-right" size={14} /></a>}
    </div>
  );
}

/* ---------- Theme ---------- */
export type Theme = 'system' | 'light' | 'dark';
const THEME_KEY = 'worksim.theme';
const THEME_EVENT = 'worksim:theme';
export function readTheme(): Theme {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
}
export function applyTheme(t: Theme) {
  const root = document.documentElement;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
}
export function setTheme(t: Theme) {
  try {
    if (t === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, t);
  } catch {
    /* preference only */
  }
  applyTheme(t);
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: t }));
}
/** The chosen appearance, shared between every control on the page. */
export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, set] = useState<Theme>(() => readTheme());
  useEffect(() => {
    applyTheme(theme);
    const on = (e: Event) => set((e as CustomEvent<Theme>).detail);
    window.addEventListener(THEME_EVENT, on);
    return () => window.removeEventListener(THEME_EVENT, on);
  }, [theme]);
  return [theme, setTheme];
}
const THEME_OPTIONS: { id: Theme; label: string; icon: IconName; hint: string }[] = [
  { id: 'system', label: 'System', icon: 'settings', hint: 'Follow your device setting' },
  { id: 'light', label: 'Light', icon: 'sun', hint: 'Always light' },
  { id: 'dark', label: 'Dark', icon: 'moon', hint: 'Always dark' },
];
/** Segmented control with the three appearance options (used on the profile page and in the menu). */
export function ThemeChoice({ compact = false }: { compact?: boolean }) {
  const [theme, set] = useTheme();
  return (
    <div className={`theme-choice ${compact ? 'compact' : ''}`} role="radiogroup" aria-label="Appearance">
      {THEME_OPTIONS.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={theme === o.id} className={theme === o.id ? 'on' : ''} title={o.hint} onClick={() => set(o.id)}>
          <Icon name={o.icon} size={16} />
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}
/** Top-bar appearance menu: a labeled popover with System, Light and Dark. */
export function ThemeToggle() {
  const [theme] = useTheme();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const current = THEME_OPTIONS.find((o) => o.id === theme) ?? THEME_OPTIONS[0]!;
  return (
    <div className="theme-menu" ref={wrap}>
      <button type="button" className="ghost icon-button" aria-haspopup="true" aria-expanded={open} aria-label={`Appearance: ${current.label}. Change theme`} title="Appearance" onClick={() => setOpen((v) => !v)}>
        <Icon name={theme === 'dark' ? 'moon' : theme === 'light' ? 'sun' : 'settings'} />
      </button>
      {open && (
        <div className="theme-popover card" role="group" aria-label="Appearance">
          <span className="eyebrow">Appearance</span>
          <ThemeChoice compact />
        </div>
      )}
    </div>
  );
}
